import { Component, OnInit, OnDestroy, ViewChild, ElementRef, ChangeDetectionStrategy } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { Table } from 'primeng/table'
import { BehaviorSubject, Observable } from 'rxjs'
import { debounceTime, distinctUntilChanged, filter, take } from 'rxjs/operators'
import { NewApiKeyModalComponent } from '../new-api-key-modal/new-api-key-modal.component'
import { EditApiKeyModalComponent } from '../edit-api-key-modal/edit-api-key-modal.component'
import { ModalDialogComponent } from 'src/app/shared/modal-dialog/modal-dialog.component'
import { UiService } from 'src/app/services/ui.service'
import { AccountService } from '../../services/account.service'
import { ApiService } from 'src/app/services/api.service'
import { ApiKeyDto, RoleDto } from 'src/app/services/dtos'
import { AccessLevelService, AccountPermission } from 'src/app/services/access-level.service'
import { SidepanelService, SidepanelController } from '../../services/sidepanel.service'
import { DateFormattingService } from 'src/app/services/date-formatting.service'
import { handleArpioError } from 'src/utils/handle-error'

export interface ApiKeyManagementItem {
  id: string
  name: string
  enabled: boolean
  roles: { id: string; name: string }[]
  roleCount: number
  createdAt: Date
  lastUpdatedAt?: Date
}

// Helper function to convert ApiKeyDto to ApiKeyManagementItem
function convertApiKeyDto(apiKey: ApiKeyDto, allRoles: RoleDto[]): ApiKeyManagementItem {
  // Map role IDs to role names
  const roleDetails: { id: string; name: string }[] = []

  if (apiKey.roles) {
    for (const roleId of apiKey.roles) {
      const role = allRoles.find((r) => r.roleId === roleId)
      if (role) {
        roleDetails.push({ id: roleId, name: role.name || roleId })
      }
    }
  }

  // Sort role details alphabetically by name
  roleDetails.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))

  return {
    id: apiKey.apiKeyId || '',
    name: apiKey.name || '',
    enabled: apiKey.enabled !== false, // default to true if not specified
    roles: roleDetails,
    roleCount: roleDetails.length,
    createdAt: apiKey.createdAt || new Date(),
    lastUpdatedAt: apiKey.lastUpdatedAt || undefined,
  }
}

@Component({
  selector: 'app-api-key-management',
  templateUrl: './api-key-management.component.html',
  host: { class: 'rbac-management-page' },
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ApiKeyManagementComponent implements OnInit, OnDestroy, SidepanelController {
  apiKeys: ApiKeyManagementItem[] = null
  selectedApiKey: ApiKeyManagementItem | null = null
  selectedApiKeyDetails: ApiKeyDto | null = null
  selectedTableItem: ApiKeyManagementItem | null = null
  showSidePanel = false
  refreshingApiKeyIds: Set<string> = new Set()

  filterText = ''
  isFilterApplied = false
  loadingFilterInput = false
  debounceSubject = new BehaviorSubject<string>('')

  @ViewChild('dt1') apiKeysTable: Table
  @ViewChild('filterInput') filterInput!: ElementRef
  @ViewChild('newApiKeyModal') newApiKeyModal: NewApiKeyModalComponent
  @ViewChild('editApiKeyModal') editApiKeyModal: EditApiKeyModalComponent
  @ViewChild('deleteApiKeyDialog', { static: true }) deleteApiKeyDialog: ModalDialogComponent
  @ViewChild('sidePanel') sidePanel: ElementRef

  allRoles: RoleDto[] = []

  // Permission properties
  canManageApiKeys$: Observable<boolean>
  canViewApiKeys$: Observable<boolean>
  unauthorizedMessage$: string

  // Delete API key dialog
  apiKeyToDelete: ApiKeyManagementItem | null = null

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private uiService: UiService,
    private accountService: AccountService,
    private apiService: ApiService,
    private accessLevelService: AccessLevelService,
    private sidepanelService: SidepanelService,
    private dateFormattingService: DateFormattingService
  ) {}

  ngOnInit() {
    // Initialize permission observables
    this.canManageApiKeys$ = this.accessLevelService.hasAccountPermission(AccountPermission.MANAGE_API_KEYS)
    this.canViewApiKeys$ = this.accessLevelService.hasAccountPermission(AccountPermission.READ_API_KEY)
    this.unauthorizedMessage$ = this.accessLevelService.getUnauthorizedMessage(AccountPermission.READ_API_KEY)

    // Redirect to account root if user doesn't have view permission
    this.canViewApiKeys$.subscribe((value) => {
      if (!value) {
        this.router.navigate([`/${this.accountService.accountId}`])
      }
    })

    this.setupDebounceFilter()
    this.checkForApiKeySelection()

    // Wait for the account to finish loading before fetching data; on a hard refresh
    // ngOnInit runs before AccountShellComponent.loadAccount$ resolves, so reading
    // accountService.accountId synchronously would yield undefined and 404 the request.
    this.accountService.accountIdStream
      .pipe(
        filter((accountId) => !!accountId),
        take(1)
      )
      .subscribe(() => this.loadData())

    // Register with sidepanel service
    this.sidepanelService.register(this, this)
  }

  private async loadData(): Promise<void> {
    try {
      // Load roles and API keys in parallel
      const [roleDtos, apiKeyDtos] = await Promise.all([
        this.apiService.listRoles(this.accountService.accountId).toPromise(),
        this.apiService.listApiKeys(this.accountService.accountId).toPromise(),
      ])

      this.allRoles = roleDtos || []

      // Process API keys with loaded roles
      this.apiKeys = (apiKeyDtos || [])
        .map((dto) => convertApiKeyDto(dto, this.allRoles))
        .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))

      this.refreshingApiKeyIds.clear()

      // Update selected API key reference if it exists
      if (this.selectedApiKey) {
        const updatedApiKey = this.apiKeys.find((k) => k.id === this.selectedApiKey.id)
        if (updatedApiKey) {
          this.selectedApiKey = updatedApiKey
          this.selectedTableItem = updatedApiKey
        }
      }
    } catch (error) {
      console.error('Error loading data:', error)
      this.allRoles = []
      this.apiKeys = []
      this.refreshingApiKeyIds.clear()
    }
  }

  private checkForApiKeySelection() {
    this.route.queryParams.subscribe((params) => {
      const apiKeyToSelect = params['selectApiKey']
      if (apiKeyToSelect) {
        this.selectApiKeyWhenLoaded(apiKeyToSelect)
      }
    })
  }

  private selectApiKeyWhenLoaded(apiKeyName: string) {
    if (this.apiKeys && this.apiKeysTable) {
      const apiKey = this.apiKeys.find((k) => k.name === apiKeyName)
      if (apiKey) {
        this.selectedApiKey = apiKey
        this.selectedTableItem = apiKey
        // @ViewChild may be undefined if the table hasn't rendered yet (loading / @if-guarded).
        this.apiKeysTable?.selection.set(apiKey)
        this.openSidePanel()
      }
    } else {
      // Retry after data loads
      setTimeout(() => this.selectApiKeyWhenLoaded(apiKeyName), 100)
    }
  }

  ngOnDestroy() {
    // Unregister from sidepanel service
    this.sidepanelService.unregister(this)
  }

  private setupDebounceFilter() {
    this.debounceSubject.pipe(debounceTime(600), distinctUntilChanged()).subscribe((filterText: string) => {
      this.applyFilter(filterText)
      this.loadingFilterInput = false
    })
  }

  onRowSelect(apiKey: ApiKeyManagementItem) {
    this.selectedApiKey = apiKey
    this.selectedTableItem = apiKey
    this.openSidePanel()
  }

  private openSidePanel() {
    if (this.selectedApiKey) {
      this.showSidePanel = true
      this.selectedApiKeyDetails = null

      // Load full API key details when sidepanel opens
      this.loadApiKeyDetails()
    }
  }

  private loadApiKeyDetails() {
    if (!this.selectedApiKey) return

    this.apiService.getApiKey(this.accountService.accountId, this.selectedApiKey.id).subscribe(
      (apiKeyDto) => {
        this.selectedApiKeyDetails = apiKeyDto
      },
      handleArpioError(window.location.href, this.apiService, this.uiService)
    )
  }

  closeSidePanel() {
    this.showSidePanel = false
    this.selectedApiKey = null
    this.selectedApiKeyDetails = null
    this.selectedTableItem = null
  }

  filterGlobal(event: any) {
    this.loadingFilterInput = true
    const filterText = event.target.value
    this.debounceSubject.next(filterText)
  }

  applyFilter(filterText: string) {
    if (!this.apiKeysTable || typeof filterText !== 'string') return

    const filterValue = filterText.toLowerCase().trim()

    if (!filterValue) {
      this.apiKeysTable.clear()
      this.filterText = filterValue
      this.isFilterApplied = false
      return
    }

    this.apiKeysTable.filterGlobal(filterValue, 'contains')
    this.filterText = filterValue
    this.isFilterApplied = true
  }

  clearInput() {
    this.filterText = ''
    this.filterInput.nativeElement.value = ''
    this.isFilterApplied = false
    this.apiKeysTable.clear()
  }

  openNewApiKeyModal() {
    this.newApiKeyModal.open()
  }

  openEditApiKeyModal() {
    if (this.selectedApiKey) {
      this.editApiKeyModal.open(this.selectedApiKey)
    }
  }

  onApiKeyCreated() {
    // Refresh the API keys data when a new key is created
    this.closeSidePanel()
    this.loadData()
  }

  onApiKeyUpdated(apiKey: ApiKeyManagementItem) {
    // Add the API key to the refreshing set to show spinner
    this.refreshingApiKeyIds.add(apiKey.id)
    // Keep track of which key was selected before refresh
    const wasApiKeySelected = this.selectedApiKey?.id === apiKey.id
    // Refresh the data
    this.loadData().then(() => {
      // If the same key was selected, reopen the side panel with updated data
      if (wasApiKeySelected && this.selectedApiKey) {
        this.openSidePanel()
      }
    })
  }

  isApiKeyRefreshing(apiKey: ApiKeyManagementItem): boolean {
    return this.refreshingApiKeyIds.has(apiKey.id)
  }

  apiKeyTrackBy(_index: number, apiKey: ApiKeyManagementItem) {
    return apiKey.id
  }

  getLastModifiedDisplay(createdAt: Date | null): string {
    return this.dateFormattingService.getRelativeTimeDisplay(createdAt, 'Never')
  }

  getLastModifiedTooltip(createdAt: Date | null): string | null {
    return this.dateFormattingService.getRelativeTimeTooltip(createdAt)
  }

  get globalFilterFields() {
    return ['name', 'id']
  }

  customSort(event: any) {
    const field = event.field
    const order = event.order

    event.data.sort((a: ApiKeyManagementItem, b: ApiKeyManagementItem) => {
      switch (field) {
        case 'name':
          return a.name.localeCompare(b.name) * order

        case 'enabled':
          return (Number(a.enabled) - Number(b.enabled)) * order

        case 'roleCount':
          // Sort by role count, then by highest access level of roles
          if (a.roleCount !== b.roleCount) {
            return (a.roleCount - b.roleCount) * order
          }
          // If same count, sort by the role with highest access level
          const aHighest = this.getHighestAccessLevelForApiKey(a)
          const bHighest = this.getHighestAccessLevelForApiKey(b)
          return (aHighest - bHighest) * order

        case 'createdAt':
          const aDate = new Date(a.createdAt).getTime()
          const bDate = new Date(b.createdAt).getTime()
          return (aDate - bDate) * order

        default:
          return 0
      }
    })
  }

  private getHighestAccessLevelForApiKey(apiKey: ApiKeyManagementItem): number {
    if (!apiKey.roles || apiKey.roles.length === 0) {
      return -1
    }

    const hierarchy = this.accessLevelService.getAccessLevelHierarchy()
    let highestIndex = -1

    // Look up each role in allRoles to get its access level
    for (const roleInfo of apiKey.roles) {
      const fullRole = this.allRoles.find((r) => r.roleId === roleInfo.id)
      if (fullRole) {
        // Check for policies with access levels
        if (fullRole.policies && fullRole.policies.length > 0) {
          for (const policy of fullRole.policies) {
            const index = hierarchy.indexOf(policy.accessLevel as any)
            if (index > highestIndex) {
              highestIndex = index
            }
          }
        }
      }
    }

    return highestIndex
  }

  deleteApiKey() {
    if (!this.selectedApiKey) return

    this.apiKeyToDelete = this.selectedApiKey
    this.deleteApiKeyDialog.open()
  }

  confirmDeleteApiKey() {
    if (!this.apiKeyToDelete) return

    this.apiService.deleteApiKey(this.accountService.accountId, this.apiKeyToDelete.id).subscribe(
      () => {
        this.deleteApiKeyDialog.close()
        this.closeSidePanel()
        this.loadData()
      },
      handleArpioError(window.location.href, this.apiService, this.uiService, {
        errorContext: { dialogId: 'delete-api-key-confirm' },
      })
    )
  }

  cancelDeleteApiKey() {
    this.apiKeyToDelete = null
    this.deleteApiKeyDialog.close()
  }

  // Get role names for the selected API key
  getApiKeyRoles(): { id: string; name: string }[] {
    if (!this.selectedApiKey) {
      return []
    }
    return this.selectedApiKey.roles
  }

  // Get tooltip for additional roles (beyond first 2 displayed)
  getAdditionalRolesTooltip(apiKey: ApiKeyManagementItem): string {
    if (apiKey.roles.length <= 2) return ''
    const additionalRoles = apiKey.roles.slice(2)
    return additionalRoles.map((role) => `<span class="tooltip-badge">${role.name}</span>`).join('')
  }

  navigateToRole(roleName: string) {
    this.router.navigate([`/${this.accountService.accountId}/settings/role-management`], {
      queryParams: { selectRole: roleName },
    })
  }
}
